export type FloatingPlacement = "top" | "top-right" | "right" | "bottom-right" | "bottom" | "bottom-left" | "left" | "top-left";

type FloatingPosition = {
  top: number;
  left: number;
  placement: FloatingPlacement;
};

type ResolveFloatingPositionOptions = {
  anchorRect: DOMRect;
  floatingRect: DOMRect;
  placement: FloatingPlacement;
  offset: number;
  viewportPadding: number;
  viewportWidth: number;
  viewportHeight: number;
};

const OPPOSITE_PLACEMENT: Record<FloatingPlacement, FloatingPlacement> = {
  top: "bottom",
  "top-right": "bottom-right",
  "top-left": "bottom-left",
  bottom: "top",
  "bottom-right": "top-right",
  "bottom-left": "top-left",
  left: "right",
  right: "left",
};

const clamp = (value: number, min: number, max: number) => {
  if (min > max) return min;
  return Math.min(Math.max(value, min), max);
};

const getCoordinates = (placement: FloatingPlacement, anchorRect: DOMRect, floatingRect: DOMRect, offset: number) => {
  switch (placement) {
    case "top-right":
      return {
        top: anchorRect.top - floatingRect.height - offset,
        left: anchorRect.left,
      };
    case "top-left":
      return {
        top: anchorRect.top - floatingRect.height - offset,
        left: anchorRect.right - floatingRect.width,
      };
    case "top":
      return {
        top: anchorRect.top - floatingRect.height - offset,
        left: anchorRect.left + anchorRect.width / 2 - floatingRect.width / 2,
      };
    case "bottom-left":
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.right - floatingRect.width,
      };
    case "bottom":
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.left + anchorRect.width / 2 - floatingRect.width / 2,
      };
    case "right":
      return {
        top: anchorRect.top + anchorRect.height / 2 - floatingRect.height / 2,
        left: anchorRect.right + offset,
      };
    case "left":
      return {
        top: anchorRect.top + anchorRect.height / 2 - floatingRect.height / 2,
        left: anchorRect.left - floatingRect.width - offset,
      };
    case "bottom-right":
    default:
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.left,
      };
  }
};

const getAvailableSpace = (placement: FloatingPlacement, anchorRect: DOMRect, viewportWidth: number, viewportHeight: number) => {
  if (placement.startsWith("bottom")) {
    return viewportHeight - anchorRect.bottom;
  }
  if (placement === "right") {
    return viewportWidth - anchorRect.right;
  }
  if (placement === "left") {
    return anchorRect.left;
  }
  return anchorRect.top;
};

const getFloatingSizeWithOffset = (placement: FloatingPlacement, floatingRect: DOMRect, offset: number) => {
  if (placement === "left" || placement === "right") {
    return floatingRect.width + offset;
  }
  return floatingRect.height + offset;
};

export const resolveFloatingPosition = ({
  anchorRect,
  floatingRect,
  placement,
  offset,
  viewportPadding,
  viewportWidth,
  viewportHeight,
}: ResolveFloatingPositionOptions): FloatingPosition => {
  const oppositePlacement = OPPOSITE_PLACEMENT[placement];
  const requestedSpace = getAvailableSpace(placement, anchorRect, viewportWidth, viewportHeight);
  const oppositeSpace = getAvailableSpace(oppositePlacement, anchorRect, viewportWidth, viewportHeight);
  const floatingSizeWithOffset = getFloatingSizeWithOffset(placement, floatingRect, offset);
  const shouldFlip = requestedSpace < floatingSizeWithOffset && oppositeSpace > requestedSpace;
  const resolvedPlacement = shouldFlip ? oppositePlacement : placement;

  const coordinates = getCoordinates(resolvedPlacement, anchorRect, floatingRect, offset);
  const maxLeft = Math.max(viewportPadding, viewportWidth - floatingRect.width - viewportPadding);
  const maxTop = Math.max(viewportPadding, viewportHeight - floatingRect.height - viewportPadding);

  return {
    left: clamp(coordinates.left, viewportPadding, maxLeft),
    top: clamp(coordinates.top, viewportPadding, maxTop),
    placement: resolvedPlacement,
  };
};
